import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import QuillCursors from 'quill-cursors';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { QuillBinding } from 'y-quill';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import 'quill/dist/quill.snow.css';
import 'quill-cursors/css';

const DOC_ID = 'doc1';
const ROOM_ID = 'room-1';
const USER_STORAGE_KEY = 'collab-editor-user-v1';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

if (!Quill.imports['modules/cursors']) {
  Quill.register('modules/cursors', QuillCursors);
}

function toUint8Array(data) {
  if (!data) {
    return null;
  }

  if (Array.isArray(data)) {
    return new Uint8Array(data);
  }

  if (Array.isArray(data?.data)) {
    return new Uint8Array(data.data);
  }

  return null;
}

function generateColorFromId(id) {
  // Deterministic color generation from user ID
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 70% 45%)`;
}

function getStoredUser() {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.id && parsed?.name) {
        return {
          ...parsed,
          color: generateColorFromId(parsed.id)
        };
      }
    } catch (error) {
      console.warn('Invalid stored user profile, generating a new one.');
    }
  }

  const id = uuidv4();
  const number = Math.floor(Math.random() * 9000) + 1000;
  const nextUser = {
    id,
    number,
    name: `User-${number}`
  };

  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
  return {
    ...nextUser,
    color: generateColorFromId(id)
  };
}

export default function Editor() {
  const editorRef = useRef();
  const editorPanelRef = useRef();
  const userRef = useRef(getStoredUser());
  const ydocRef = useRef(null);
  const ytextRef = useRef(null);
  const providerRef = useRef(null);
  const remoteCursorIdsRef = useRef(new Set());
  const [connectionState, setConnectionState] = useState('connecting');
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [lastSavedAtLabel, setLastSavedAtLabel] = useState('');
  const sessionTrackerRef = useRef(null);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/history/${DOC_ID}`);
      const versions = Array.isArray(res.data) ? res.data : [];
      setHistoryError('');
      
      // Group versions into sessions (3+ minutes apart = new session)
      const sessions = [];
      let currentSession = null;
      const SESSION_GAP_MS = 180000; // 3 minutes

      versions.forEach((version, idx) => {
        const timestamp = new Date(version.timestamp).getTime();
        
        if (!currentSession || (timestamp - currentSession.endTime) > SESSION_GAP_MS) {
          // Start new session
          if (currentSession) sessions.push(currentSession);
          currentSession = {
            startTime: timestamp,
            endTime: timestamp,
            versionIdx: idx,
            changeCount: 1,
            version: version
          };
        } else {
          // Continue current session
          currentSession.endTime = timestamp;
          currentSession.changeCount += 1;
          currentSession.versionIdx = idx; // Latest version in session
          currentSession.version = version; // Latest snapshot
        }
      });
      
      if (currentSession) sessions.push(currentSession);
      setHistory(sessions.reverse().slice(0, 20));
    } catch (error) {
      console.warn('Failed to load history:', error.message);
      setHistoryError('Unable to load history. Check backend URL and CORS settings.');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreSession = async (session) => {
    const ydoc = ydocRef.current;
    const ytext = ytextRef.current;

    if (!ydoc || !ytext || !session?.version) {
      return;
    }

    const update = toUint8Array(session.version?.data);
    if (!update) {
      return;
    }

    const snapshotDoc = new Y.Doc();
    Y.applyUpdate(snapshotDoc, update);
    const snapshotText = snapshotDoc.getText('quill').toString();
    snapshotDoc.destroy();

    ydoc.transact(() => {
      ytext.delete(0, ytext.length);
      ytext.insert(0, snapshotText);
    });

    const encodedState = Y.encodeStateAsUpdate(ydoc);
    await axios.post(`${API_BASE_URL}/save`, {
      docId: DOC_ID,
      state: Array.from(encodedState)
    });
    
    // Clear session tracker after restore to start fresh
    if (sessionTrackerRef.current) {
      sessionTrackerRef.current = null;
    }
    
    await loadHistory();
  };

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    // Remove any auto-generated Quill toolbar from prior mounts.
    if (editorPanelRef.current) {
      editorPanelRef.current.querySelectorAll('.ql-toolbar').forEach((toolbar) => toolbar.remove());
    }

    // Reset editor host node before creating a new Quill instance.
    editorRef.current.innerHTML = '';

    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      bounds: editorRef.current,
      modules: {
        toolbar: [['bold', 'italic', 'underline']],
        cursors: true
      }
    });
    const cursors = quill.getModule('cursors');

    const ydoc = new Y.Doc();
    ydocRef.current = ydoc;

    const provider = new WebsocketProvider(
      WS_BASE_URL,
      ROOM_ID,
      ydoc
    );
    providerRef.current = provider;

    const ytext = ydoc.getText('quill');
    ytextRef.current = ytext;

    new QuillBinding(ytext, quill, provider.awareness);

    const updatePresenceList = () => {
      const states = Array.from(provider.awareness.getStates().values());
      const seen = new Set();
      const nextUsers = states
        .map((state) => ({
          ...state.user,
          editing: !!state.editing
        }))
        .filter((user) => user?.id && !seen.has(user.id) && seen.add(user.id));

      if (!nextUsers.some((user) => user.id === userRef.current.id)) {
        nextUsers.unshift({ ...userRef.current, editing: false });
      }
      setUsers(nextUsers);
    };

    const updateRemoteCursors = () => {
      const awarenessStates = Array.from(provider.awareness.getStates().values());
      const activeRemoteIds = new Set();

      awarenessStates.forEach((state) => {
        const remoteUser = state?.user;
        const selection = state?.selection;
        if (!remoteUser?.id || remoteUser.id === userRef.current.id || !selection) {
          return;
        }

        activeRemoteIds.add(remoteUser.id);
        cursors.createCursor(remoteUser.id, remoteUser.name, remoteUser.color);
        cursors.moveCursor(remoteUser.id, selection);
      });

      remoteCursorIdsRef.current.forEach((cursorId) => {
        if (!activeRemoteIds.has(cursorId)) {
          cursors.removeCursor(cursorId);
        }
      });
      remoteCursorIdsRef.current = activeRemoteIds;
    };

    const publishLocalPresence = (overrides = {}) => {
      provider.awareness.setLocalState({
        user: userRef.current,
        editing: false,
        selection: null,
        ...overrides
      });
    };

    publishLocalPresence();

    let editingTimer;
    const handleSelectionChange = (range) => {
      publishLocalPresence({
        selection: range ? { index: range.index, length: range.length } : null,
        editing: !!range
      });
    };

    const handleTextChange = (_delta, _oldDelta, source) => {
      if (source !== 'user') {
        return;
      }

      const range = quill.getSelection();
      publishLocalPresence({
        selection: range ? { index: range.index, length: range.length } : null,
        editing: true
      });

      clearTimeout(editingTimer);
      editingTimer = setTimeout(() => {
        const latestRange = quill.getSelection();
        publishLocalPresence({
          selection: latestRange ? { index: latestRange.index, length: latestRange.length } : null,
          editing: false
        });
      }, 1200);
    };

    const handleAwarenessChange = () => {
      updatePresenceList();
      updateRemoteCursors();
    };

    const handleStatus = ({ status }) => {
      setConnectionState(status);
      if (status === 'connected') {
        publishLocalPresence();
        updatePresenceList();
      }
    };

    provider.awareness.on('change', handleAwarenessChange);
    provider.on('status', handleStatus);
    quill.on('selection-change', handleSelectionChange);
    quill.on('text-change', handleTextChange);
    handleAwarenessChange();

    // Load doc
    axios.get(`${API_BASE_URL}/load/${DOC_ID}`)
      .then(res => {
        if (res.data?.state) {
          const update = new Uint8Array(res.data.state);
          Y.applyUpdate(ydoc, update);
        }
      });
    loadHistory();

    // Session-based auto save with throttled checkpoints.
    let lastChangeTime = Date.now();
    let lastSavedAt = Date.now();
    let firstUnsavedChangeAt = null;
    const SESSION_INACTIVITY_MS = 180000; // 3 minutes
    const ACTIVE_SAVE_MS = 60000; // 60 seconds
    const INITIAL_CHECKPOINT_MS = 10000; // first checkpoint after 10 seconds of edits
    let pendingSave = false;
    let saveInFlight = false;

    const handleDocChange = () => {
      lastChangeTime = Date.now();
      if (!pendingSave) {
        firstUnsavedChangeAt = Date.now();
      }
      pendingSave = true;
    };

    ydoc.on('update', handleDocChange);

    const persistSnapshot = async (refreshHistory = false) => {
      if (saveInFlight) {
        return;
      }
      saveInFlight = true;
      const state = Y.encodeStateAsUpdate(ydoc);
      try {
        await axios.post(`${API_BASE_URL}/save`, {
          docId: DOC_ID,
          state: Array.from(state)
        });
        lastSavedAt = Date.now();
        setLastSavedAtLabel(new Date(lastSavedAt).toLocaleTimeString());
        setHistoryError('');
        if (refreshHistory) {
          await loadHistory();
        }
      } finally {
        saveInFlight = false;
      }
    };

    if (window.location.hostname !== 'localhost' && API_BASE_URL.includes('localhost')) {
      setHistoryError('Frontend is still pointing to localhost API. Set Vercel env vars and redeploy.');
    }

    const saveInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceChange = Date.now() - lastChangeTime;
      const timeSinceSave = now - lastSavedAt;
      const timeSinceFirstUnsaved = firstUnsavedChangeAt ? now - firstUnsavedChangeAt : 0;

      if (pendingSave && firstUnsavedChangeAt && lastSavedAt < firstUnsavedChangeAt && timeSinceFirstUnsaved >= INITIAL_CHECKPOINT_MS) {
        // First checkpoint shortly after edits begin so history appears quickly.
        persistSnapshot(true).catch(() => {
          setHistoryError('Save failed. Check backend availability and CLIENT_URL in Render.');
        });
        return;
      }
      
      if (pendingSave && timeSinceChange >= SESSION_INACTIVITY_MS) {
        // Session ended: persist one final checkpoint and refresh timeline.
        persistSnapshot(true)
          .then(() => {
            pendingSave = false;
            firstUnsavedChangeAt = null;
          })
          .catch(() => {
            // Ignore intermittent save errors and retry on next tick.
            setHistoryError('Save failed. Check backend availability and CLIENT_URL in Render.');
          });
      } else if (pendingSave && timeSinceSave >= ACTIVE_SAVE_MS) {
        // During active work: periodic safety checkpoint and timeline refresh.
        persistSnapshot(true).catch(() => {
          // Ignore intermittent save errors and retry on next tick.
          setHistoryError('Save failed. Check backend availability and CLIENT_URL in Render.');
        });
      }
    }, 1000);

    return () => {
      clearTimeout(editingTimer);
      clearInterval(saveInterval);
      ydoc.off('update', handleDocChange);
      quill.off('selection-change', handleSelectionChange);
      quill.off('text-change', handleTextChange);
      provider.off('status', handleStatus);
      provider.awareness.off('change', handleAwarenessChange);
      provider.awareness.setLocalState(null);
      provider.destroy();
      ydoc.destroy();
      remoteCursorIdsRef.current.clear();
      setUsers([]);
    };

  }, []);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white/90 shadow-xl p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Collaborative Document</h2>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${connectionState === 'connected' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {connectionState === 'connected' ? 'Connected' : 'Connecting...'}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-semibold text-slate-700">Online Users</h3>
          <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: u?.color }} />
                  <span className="text-sm font-medium text-slate-800">{u?.name}</span>
                </div>
                <span className={`text-xs font-semibold ${u?.editing ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {u?.editing ? 'editing' : 'online'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div ref={editorPanelRef} className="rounded-3xl border border-slate-200 bg-white shadow-xl p-4 sm:p-5 overflow-hidden flex flex-col">
          <div
            ref={editorRef}
            className="editor-surface pointer-events-auto min-h-[320px] h-[50vh] max-h-[680px] sm:min-h-[440px]"
          />
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white shadow-xl p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Revision History</h3>
            <div className="flex items-center gap-2">
              {historyLoading && <span className="text-xs text-slate-500">loading...</span>}
              <button
                type="button"
                onClick={loadHistory}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>
          </div>
          {!!lastSavedAtLabel && (
            <p className="mt-2 text-xs text-slate-500">Last checkpoint: {lastSavedAtLabel}</p>
          )}
          {!!historyError && (
            <p className="mt-2 text-xs text-rose-600">{historyError}</p>
          )}
          <div className="mt-3 max-h-[460px] overflow-y-auto space-y-2 pr-1">
            {!history.length && !historyLoading && (
              <p className="text-sm text-slate-500">No activity sessions yet.</p>
            )}
            {history.map((session, idx) => {
              const startDate = new Date(session.startTime);
              const durationSec = Math.round((session.endTime - session.startTime) / 1000);
              const timeDisplay = durationSec < 60 ? `${durationSec}s` : `${Math.round(durationSec / 60)}m`;
              
              return (
                <button
                  key={`session-${idx}`}
                  type="button"
                  onClick={() => restoreSession(session)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-left text-sm transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <div className="font-medium text-slate-800">Session {history.length - idx}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {startDate.toLocaleTimeString()} ({timeDisplay})
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

